import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AdjuvantDisplay, AdjuvantUnitDisplay, ApplicationTimepointDisplay, ApplicationVolumeDisplay, HerbicideTradeNameDisplay, RateUnitDisplay, SprayBoothStudyCopyArgs, SprayBoothStudyFormComponent, SprayStudyOptions } from './spray-booth-study-form/spray-booth-study-form.component';
import { CeRequestOptions } from '../../request-header/request-header.component';
import { asEnumerable } from 'linq-es2015';
import { ActiveModel, AdjuvantModel } from '../../../shared/services/spray-booth-study.service';
import { RecurrencePattern } from "../../../shared/services/request.service";
import { ExperimentDesignFormValidatorsService } from '../experiment-design-form/experiment-design-form-validators.service';
import { ExperimentDesignFormBuilderService } from '../experiment-design-form/experiment-design-form-builder.service';

@Component({
  selector: 'app-spray-booth-study',
  templateUrl: './spray-booth-study.component.html',
  styleUrls: ['./spray-booth-study.component.css'],
})
export class SprayBoothStudyComponent implements OnInit, AfterViewInit {
  @Input() experimentDesignFormGroup: FormGroup;
  @Input() ceRequestOptions: CeRequestOptions;
  public activeOptions: ActiveModel[];
  public adjuvantOptions: AdjuvantModel[];
  public sprayStudyOptions: SprayStudyOptions;

  public isSprayBooth: boolean;
  private recurrencePattern: boolean;
  public tooltipText: string;

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private validators: ExperimentDesignFormValidatorsService
  ) { }

  @ViewChildren(SprayBoothStudyFormComponent)
  public sprayBoothStudyForms: QueryList<SprayBoothStudyFormComponent>;

  ngOnInit() {
    this.getSprayStudyOptions();

    this.setTooltipText();
    this.isSprayBooth = this.experimentDesignFormGroup.get('sprayStudy').value === true;

    this.experimentDesignFormGroup.get('sprayStudy').valueChanges.subscribe(val => {
      this.isSprayBooth = val === true;

      const treatments = this.experimentDesignFormGroup.get('treatments') as FormArray;
      if (treatments && treatments.controls) {
        for (let i = 0; i < treatments.length; i++) {
          const treatment = treatments.at(i) as FormGroup;
          const sprayBoothStudy = treatment.get('sprayBoothStudy') as FormArray;

          if (val === true && sprayBoothStudy && sprayBoothStudy.length === 0) {
            const sprayEventGroup = this.fb.group({
              applicationVolumeId: [null, Validators.required],
              applicationTimepointId: [null, Validators.required],
              actives: this.fb.array([]),
              adjuvants: this.fb.array([]),
            });

            sprayEventGroup.setValidators(this.validators.atLeastOneActiveOrAdjuvantValidator());
            sprayBoothStudy.push(sprayEventGroup);
            sprayEventGroup.updateValueAndValidity();
          }

          this.toggleSprayBoothValidators(treatment, val === true);
        }
      }
    });
    this.experimentDesignFormGroup.get('recurrencePattern').valueChanges.subscribe(val => {
      this.setTooltipText();
    });
    this.activeOptions = this.ceRequestOptions.actives;
    this.adjuvantOptions = this.ceRequestOptions.adjuvants;
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  private toggleSprayBoothValidators(treatment: FormGroup, enabled: boolean): void {
    const sprayBoothStudy = treatment.get('sprayBoothStudy') as FormArray;

    if (sprayBoothStudy && sprayBoothStudy.controls) {
      for (let j = 0; j < sprayBoothStudy.length; j++) {
        const sprayGroup = sprayBoothStudy.at(j) as FormGroup;

        this.toggleSprayEventFields(sprayGroup, enabled);
        this.toggleActivesValidators(sprayGroup.get('actives') as FormArray, enabled);
        this.toggleAdjuvantsValidators(sprayGroup.get('adjuvants') as FormArray, enabled);

        if (enabled) {
          sprayGroup.setValidators(this.validators.atLeastOneActiveOrAdjuvantValidator());
        } else {
          sprayGroup.clearValidators();
        }

        sprayGroup.updateValueAndValidity();
      }
    }
  }

  private toggleSprayEventFields(sprayGroup: FormGroup, enabled: boolean): void {
    const volume = sprayGroup.get('applicationVolumeId');
    const timepoint = sprayGroup.get('applicationTimepointId');

    if (enabled) {
      volume.setValidators([Validators.required]);
      timepoint.setValidators([Validators.required]);
    } else {
      volume.clearValidators();
      timepoint.clearValidators();
    }

    volume.updateValueAndValidity();
    timepoint.updateValueAndValidity();
  }

  private toggleActivesValidators(actives: FormArray, enabled: boolean): void {
    if (!actives) { return; }

    for (let k = 0; k < actives.length; k++) {
      const group = actives.at(k) as FormGroup;
      const activeId = group.get('activeId');
      const rateValue = group.get('rateValue');
      const rateUnitId = group.get('rateUnitId');
      const relativeFieldRate = group.get('relativeFieldRate');

      if (enabled) {
        activeId.setValidators([Validators.required]);
        rateValue.setValidators([Validators.required, Validators.min(0)]);
        rateUnitId.setValidators([Validators.required]);
        relativeFieldRate.setValidators([Validators.required]);
      } else {
        activeId.clearValidators();
        rateValue.clearValidators();
        rateUnitId.clearValidators();
        relativeFieldRate.clearValidators();
      }

      activeId.updateValueAndValidity();
      rateValue.updateValueAndValidity();
      rateUnitId.updateValueAndValidity();
      relativeFieldRate.updateValueAndValidity();
    }
  }

  private toggleAdjuvantsValidators(adjuvants: FormArray, enabled: boolean): void {
    if (!adjuvants) { return; }

    for (let k = 0; k < adjuvants.length; k++) {
      const group = adjuvants.at(k) as FormGroup;
      const adjuvantId = group.get('adjuvantId');
      const adjuvantValue = group.get('adjuvantValue');

      if (enabled) {
        adjuvantId.setValidators([Validators.required]);
        adjuvantValue.setValidators([Validators.required, Validators.min(0)]);
      } else {
        adjuvantId.clearValidators();
        adjuvantValue.clearValidators();
      }

      adjuvantId.updateValueAndValidity();
      adjuvantValue.updateValueAndValidity();
    }
  }


  disableFormValidation() {
    const sprayControl = this.experimentDesignFormGroup.get('sprayStudy');
    const treatments = this.experimentDesignFormGroup.get('treatments') as FormArray;
    const firstTreatment = treatments.at(0) as FormGroup;
    const sprayBoothStudy = firstTreatment.get('sprayBoothStudy') as FormArray;

    if (sprayControl && sprayBoothStudy) {
      const isSprayEnabled = sprayControl.value === true;

      for (let i = 0; i < sprayBoothStudy.length; i++) {
        const group = sprayBoothStudy.at(i) as FormGroup;
        const controls = group.controls;

        for (const key in controls) {
          if (controls.hasOwnProperty(key)) {
            const control = controls[key];

            if (isSprayEnabled) {
              if (key === 'applicationTimepointId' || key === 'applicationVolumeId') {
                control.setValidators([Validators.required]);
              } else if (key === 'actives' || key === 'adjuvants') {
              } else {
                control.setValidators(null);
              }
            } else {
              control.clearValidators();
              control.setErrors(null);
            }

            control.updateValueAndValidity({ onlySelf: true });
          }
        }

        if (isSprayEnabled) {
          group.setValidators(null);
        } else {
          group.clearValidators();
          group.setErrors(null);
        }

        group.updateValueAndValidity({ onlySelf: true });
      }

      if (isSprayEnabled) {
        sprayBoothStudy.setValidators(null);
      } else {
        sprayBoothStudy.clearValidators();
        sprayBoothStudy.setErrors(null);
      }

      sprayBoothStudy.updateValueAndValidity({ onlySelf: true });
    }
  }

  setTooltipText(val: any = null) {
    const recurrence = val != null ? val : this.experimentDesignFormGroup.get('recurrencePattern').value;
    const sprayControl = this.experimentDesignFormGroup.get('sprayStudy');
    this.recurrencePattern = recurrence === RecurrencePattern.Weekly;
    if (this.recurrencePattern) {
      sprayControl.setValue(false);
      sprayControl.disable();
      this.tooltipText = 'Spray Booth Study Instructions are not available for recurring requests.';
    } else {
      sprayControl.enable();
      // tslint:disable-next-line:max-line-length
      this.tooltipText = 'Refers to chemicals that will be applied at specific timepoints for specific experimental needs. Does not refer to pesticides that may be applied for plant care purposes.';
    }
  }
  public get treatments(): FormArray {
    return this.experimentDesignFormGroup.get('treatments') as FormArray;
  }
  public get SprayStudy(): FormControl {
    return this.experimentDesignFormGroup.get('sprayStudy') as FormControl;
  }
  getSprayBoothValue(event: boolean) {
    this.isSprayBooth = event === true;
  }

  private getSprayStudyOptions() {
    const adjuvantName = asEnumerable(this.ceRequestOptions.adjuvants)
      .Where(s => s.isActive)
      .OrderBy(s => s.order)
      .ThenBy(s => s.name)
      .Select(s => {
        return <AdjuvantDisplay>{
          id: s.id,
          display: s.name
        };
      })
      .ToArray();

    const adjuvantUnit = asEnumerable(this.ceRequestOptions.adjuvantUnits)
      .Where(s => s.isActive)
      .OrderBy(s => s.order)
      .ThenBy(s => s.unit)
      .Select(s => {
        return <AdjuvantUnitDisplay>{
          id: s.id,
          display: s.unit
        };
      })
      .ToArray();

    const applicationVolume = asEnumerable(this.ceRequestOptions.applicationVolumes)
      .Where(s => s.isActive)
      .OrderBy(s => s.order)
      .ThenBy(s => s.volume)
      .Select(s => {
        return <ApplicationVolumeDisplay>{
          id: s.id,
          display: s.volume
        };
      })
      .ToArray();

    const applicationTimepoint = asEnumerable(this.ceRequestOptions.applicationTimepoints)
      .Where(s => s.isActive)
      .OrderBy(s => s.order)
      .ThenBy(s => s.timepoint)
      .Select(s => {
        return <ApplicationTimepointDisplay>{
          id: s.id,
          display: s.timepoint
        };
      })
      .ToArray();

    const rateUnit = asEnumerable(this.ceRequestOptions.rateUnits)
      .Where(s => s.isActive)
      .OrderBy(s => s.order)
      .ThenBy(s => s.unit)
      .Select(s => {
        return <RateUnitDisplay>{
          id: s.id,
          display: s.unit
        };
      })
      .ToArray();

    const herbicideTradeName = asEnumerable(this.ceRequestOptions.actives)
      .Where(s => s.isActive)
      .OrderBy(s => s.order)
      .ThenBy(s => s.herbicideTradeName)
      .Select(s => {
        return <HerbicideTradeNameDisplay>{
          id: s.id,
          display: `${s.herbicideTradeName}(${s.factsid})`
        };
      })
      .ToArray();

    this.sprayStudyOptions = {
      herbicideTradeNames: herbicideTradeName,
      rateUnits: rateUnit,
      applicationTimepoints: applicationTimepoint,
      applicationVolumes: applicationVolume,
      adjuvantNames: adjuvantName,
      adjuvantUnits: adjuvantUnit
    };
  }

  public applyToAllSprayStudies(sourceIndex: number): void {
    if (!this.sprayBoothStudyForms) {
      console.warn('sprayBoothStudyForms not initialized yet.');
      return;
    }

    const components = this.sprayBoothStudyForms.toArray();

    if (!components || components.length <= 1 || sourceIndex < 0 || sourceIndex >= components.length) {
      console.warn('Invalid index or not enough components to apply.');
      return;
    }

    const sourceComponent = components[sourceIndex];

    if (!sourceComponent || !sourceComponent.sprayBoothStudy || sourceComponent.sprayBoothStudy.length === 0) {
      console.warn('Source component or sprayBoothStudy is not ready.');
      return;
    }
    const sprayEvents: any[] = [];

    for (let i = 0; i < sourceComponent.sprayBoothStudy.length; i++) {
      const study = sourceComponent.sprayBoothStudy.at(i);
      if (study instanceof FormGroup) {
        sprayEvents.push({
          applicationVolumeId: study.get('applicationVolumeId') ? study.get('applicationVolumeId').value : null,
          applicationTimepointId: study.get('applicationTimepointId') ? study.get('applicationTimepointId').value : null,
          actives: study.get('actives') && study.get('actives') instanceof FormArray
            ? (study.get('actives') as FormArray).value
            : [],
          adjuvants: study.get('adjuvants') && study.get('adjuvants') instanceof FormArray
            ? (study.get('adjuvants') as FormArray).value
            : []
        });
      }
    }

    if (sprayEvents.length === 0) {
      console.warn('No spray events found to copy.');
      return;
    }
    const copyArgs = { sprayEvents: sprayEvents };
    for (let i = 0; i < components.length; i++) {
      const target = components[i];
      if (i !== sourceIndex && target && typeof target.copyAllSprayEvents === 'function') {
        target.copyAllSprayEvents(copyArgs);
      }
    }
  }
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-2/_work/98/s/src/app/request-creation/experiment-design/spray-booth-study/spray-booth-study.component.ts